import PropTypes from 'prop-types'
import React from 'react'
import { sortBy } from 'lodash'
import cx from 'classnames'

import Select from 'components/Form/Select'

import { compareParseInt } from 'helpers/index'
import { makeOptions } from 'helpers/components'
import moment from 'helpers/moment'

import classes from './Dropdown.scss'

export class PricelistDropdown extends React.Component {
  getBaseCollection = () => {
    return this.props.pricelist.filter(product => product &&
      (this.props.isAdmin || product.enabled) // admin can see sold out
    )
  };

  getOptions = () => {
    let collection = this.getBaseCollection()
    if (!collection || !collection.length) return []

    collection = collection.sort(this.sortList) // sort the list by the default sort
    collection = sortBy(collection, ['category.priority', 'category_id']) // re-sort the list by pricelist categories

    return makeOptions(collection, 'product_id', 'name', { sort: false, enabled: 'enabled' })
  };

  sortList = (a, b) => {
    // !enabled at bottom, then sort by type then name
    return a.type && a.type.name && b.type && b.type.name && a.type.name.localeCompare(b.type.name) ||
      a.name && b.name && a.name.localeCompare(b.name) || 0
  };

  // taken from defaultMenuRenderer react-select@1.0.0-rc https://github.com/JedWatson/react-select/tree/916ab0e
  renderMenu = (
    {
      focusedOption,
      instancePrefix,
      labelKey,
      onFocus, onSelect,
      optionClassName, optionComponent, optionRenderer,
      options,
      valueArray, valueKey,
      onOptionRef
    }
  ) => {
    let Option = optionComponent

    // wraps in a custom div as I can't figure out how to pass a class to Select-menu
    return <div className={classes.menu}>
      {options.map((option, i, optionsArray) => {
        let isSelected = valueArray && valueArray.indexOf(option) > -1
        let isFocused = option === focusedOption
        let newCategory = !optionsArray[i - 1] ||
          option.model.category_id !== optionsArray[i - 1].model.category_id

        // injects a custom classes.row as I can't figure out how to pass to optionClassName
        let optionClass = cx(classes.row, optionClassName, {
          'Select-option': true,
          'is-selected': isSelected,
          'is-focused': isFocused,
          'is-disabled': option.disabled
        })

        let optionNode = <Option className={optionClass}
          instancePrefix={instancePrefix}
          isDisabled={option.disabled}
          isFocused={isFocused}
          isSelected={isSelected}
          key={`option-${i}-${option[valueKey]}`}
          onFocus={onFocus}
          onSelect={onSelect}
          option={option}
          optionIndex={i}
          ref={ref => { onOptionRef(ref, isFocused) }}
        >
          {optionRenderer(option, i)}
        </Option>

        if (!newCategory) return optionNode

        return [
          option.model.category && !option.model.category.hidden && option.model.category.name &&
            <div className={classes['group-title']}>
              {option.model.category.name}
            </div> || null,
          optionNode
        ]
      })}
    </div>
  };

  renderOption = (option, i) => {
    const p = option.model // p = pricelistProduct
    if (!p) return option.label

    return [
      <div className={classes.column} key={`option-column-${i}-0-${option.value}`}>
        <span className={classes['column-row--bold']}>{p.name}</span>
        {p.scientific_name &&
          <span className={cx(classes['column-row--italic'], classes['column-row--unimportant'])}>
            {p.scientific_name}
          </span>
        }
        {p.details && <span className={classes['column-row--unimportant']}>{p.details}</span>}
      </div>,
      <div
        className={cx(classes.column, classes['column--center'], classes['column--italic'], classes['column--desktop'])}
        key={`option-column-${i}-1-${option.value}`}
      >
        {p.sourcing_location && <span className={classes['column-row']}>{p.sourcing_location}</span>}

        {p.tasting_notes && p.sourcing_method && <span className={classes['column-row--italic']}>{p.sourcing_method}</span>}
        {!p.tasting_notes && p.fishing_vessel && <span className={classes['column-row--italic']}>{p.fishing_vessel}</span>}

        {p.tasting_notes && <span className={classes['column-row']}>{p.tasting_notes}</span>}
        {!p.tasting_notes && p.sourcing_method && <span className={classes['column-row']}>{p.sourcing_method}</span>}
      </div>,
      <div
        className={cx(classes.column, classes['column--center'], classes['column--nowrap'])}
        key={`option-column-${i}-2-${option.value}`}
      >
        {!!p.prices.length && p.prices.filter(o => o.price).map((o, index) => {
          if (!o.option) o.option = this.props.options.find(option => compareParseInt(option.id, o.option_id))

          return <span className={classes['column-row']} key={`option-${i}-${option.value}-option[${index}]`}>
            {o.option && o.option.name || ''}
          </span>
        })}
      </div>,
      <div
        className={cx(classes.column, classes['column--right'])}
        key={`option-column-${i}-3-${option.value}`}
      >
        {p.enabled && p.prices.length && p.prices.filter(o => o.price).map((o, index) => {
          if (!o.price || !Number(o.price)) return null
          if (!o.uom) o.uom = this.props.uoms.find(uom => compareParseInt(uom.id, o.uom_id))

          return <span className={classes['column-row--nowrap']} key={`option-${i}-${option.value}-price[${index}]`}>
            <strong>{Number(o.price).formatPrice()}</strong>
            {o.uom && (o.uom.shorthand || o.uom.name) && ` /${o.uom.shorthand || o.uom.name}` || ''}
          </span>
        }) || !p.enabled && 'sold out' || ''}
      </div>
    ]
  };

  render () {
    return <Select
      {...this.props}

      placeholder='select a product'
      options={['expandable', 'pop-out']}
      selectOptions={this.getOptions()}
      menuRenderer={this.renderMenu}
      optionRenderer={this.renderOption}
      filterOption={this.props.filterOption}
      filterFields={this.props.filterFields}
      filterModelFields={this.props.filterModelFields}
      disabled={this.props.disabled}
    />
  }
}

PricelistDropdown.propTypes = {
  pricelist: PropTypes.array.isRequired,
  options: PropTypes.array.isRequired,
  uoms: PropTypes.array.isRequired,
  date: PropTypes.object,
  isAdmin: PropTypes.bool,

  label: PropTypes.string,
  disabled: PropTypes.bool,

  filterOption: PropTypes.func,
  filterFields: PropTypes.array, // custom
  filterModelFields: PropTypes.array // custom
}

PricelistDropdown.defaultProps = {
  label: 'Product',
  pricelist: [],
  date: moment('today'),
  isAdmin: false,
  disabled: false
}

export default PricelistDropdown



// WEBPACK FOOTER //
// ./src/components/Pricelist/Dropdown/Dropdown.js